import { PrismicNextLink } from '@prismicio/next';
import type { LinkField } from '@prismicio/client';
import { cn } from '@/lib/utils';

type Props = {
  field: LinkField;
  /** Extra classes merged onto the anchor. */
  className?: string;
};

/**
 * Solid-green pill CTA used by the redesign sections — mirrors the navbar's
 * "Get Started" and `CallToActionBanner`. The button text comes from the
 * Prismic link's own Text property (set via `allowText: true` on the field).
 *
 * Modal support is not this component's business: the global
 * `useModalLinkDispatcher` intercepts any click whose href resolves to a
 * `#modal-<id>` token, so a link authored with that fragment just works —
 * this component always renders an anchor and lets the dispatcher decide.
 *
 * Consumers can pass `className` when a specific layout needs to override
 * inline-vs-block or width behaviour (e.g. `flex` vs `inline-flex`).
 */
export function PrimaryGreenCtaButton({ field, className }: Props) {
  const label = (field as { text?: string }).text ?? '';

  return (
    <PrismicNextLink
      field={field}
      className={cn(
        'group/cta-btn inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-[8px] bg-[#05f273] px-6 py-3 font-inter text-[16px] font-medium leading-6 text-[#002726] transition-colors hover:bg-[#59B264] focus-visible:bg-[#59B264] active:bg-[#59B264]',
        className,
      )}
    >
      <span className="underline-offset-2 group-hover/cta-btn:underline group-focus-visible/cta-btn:underline group-active/cta-btn:underline">
        {label}
      </span>
    </PrismicNextLink>
  );
}
